import { useState, useEffect, useMemo, useCallback } from 'react';
import { Affiliate, User } from '../types';
import { authService } from '../services/authService';
import { dbService } from '../services/dbService';
import { getDefaultAffiliateByDomain } from '../utils/domainAffiliate';

export interface SelectableUser extends User {
  selected: boolean;
}

/**
 * 계열사 직원 명부 공유 캐시 (모듈 스코프).
 *
 * 이 훅은 채팅창 멘션 팝업·홈 검색·그룹 생성 모달·초대 모달 네 곳에서 각각 인스턴스를 만든다.
 * 인스턴스마다 자기 목록을 들고 있어서 채널을 옮기거나(ChatWindow 재마운트) 모달을 열 때마다
 * 전 직원 명부를 다시 받아왔다. 명부는 원격 MSSQL 조회(왕복 65~143ms)라 매번 낼 비용이 아니다.
 * RN 앱(ina-chat-rn)에는 이미 같은 캐시가 있는데 웹에만 빠져 있었다.
 *
 * in-flight promise까지 공유해 동시 마운트 시 중복 요청도 합친다 (authService.getAffiliates 패턴).
 * 명부는 인사 시스템 원본이라 세션 중 바뀔 일이 드물어 TTL 없이 새로고침까지 유지한다.
 */
const userDirectoryCache = new Map<string, User[]>();
const userDirectoryInflight = new Map<string, Promise<User[]>>();

/**
 * 캐시 키 — 승인 필터 적용 여부가 다르면 **다른 명부**다.
 *
 * ⚠ 키를 affiliateId 하나로 두면 관리자가 '로그인 승인' 탭을 먼저 열었을 때 미승인자까지 담긴
 *   목록이 그 계열사 키로 캐시되어 동료 검색·멤버 초대·멘션 후보로 그대로 새어 나간다.
 *   반대 순서면 승인 화면이 반쪽이 된다(승인할 사람이 안 보인다).
 */
function directoryKey(affiliateId: string, includeUnapproved?: boolean) {
  return includeUnapproved ? `${affiliateId}|all` : affiliateId;
}

function fetchAffiliateUsers(affiliateId: string, includeUnapproved?: boolean): Promise<User[]> {
  const key = directoryKey(affiliateId, includeUnapproved);
  const cached = userDirectoryCache.get(key);
  if (cached) return Promise.resolve(cached);

  const inflight = userDirectoryInflight.get(key);
  if (inflight) return inflight;

  const promise = dbService
    .searchUsersByAffiliate(affiliateId, undefined, { includeUnapproved })
    .then((users) => {
      userDirectoryCache.set(key, users);
      return users;
    })
    .finally(() => {
      userDirectoryInflight.delete(key);
    });

  userDirectoryInflight.set(key, promise);
  return promise;
}

/** 로그아웃 시 다른 계정의 명부가 남지 않도록 비운다 */
export function clearUserDirectoryCache() {
  userDirectoryCache.clear();
  userDirectoryInflight.clear();
}

/** user:avatar-changed 수신 시 명부 캐시의 해당 사용자만 패치한다.
 *  전체 clear는 다음 검색 오픈마다 MSSQL 전 직원 재조회(왕복 65~143ms)를 유발하는데
 *  아바타 변경은 명부 자체(인사 원본)가 바뀐 게 아니므로 과잉이다.
 *  이미 마운트된 훅 인스턴스의 목록까지는 갱신하지 않는다 — 모달을 닫았다 열면 반영
 *  (invalidateChannelMemberIndex와 같은 의도된 한계). */
export function patchUserDirectoryAvatar(userId: string | number, avatarUrl: string | null) {
  userDirectoryCache.forEach((users, affId) => {
    if (!users.some((u) => String(u.id) === String(userId))) return;
    userDirectoryCache.set(
      affId,
      users.map((u) => (String(u.id) === String(userId) ? { ...u, avatar: avatarUrl } : u))
    );
  });
}

interface UseAffiliateUserSearchOptions {
  /** 모달이 열려있는지 여부 — false이면 데이터 로딩을 하지 않음 */
  isActive: boolean;
  /** 현재 로그인한 사용자 ID (목록에서 제외) */
  currentUserId?: string | number;
  /** 제외할 사용자 ID 목록 (기존 멤버 등) */
  excludeUserIds?: string[];
  /** 기본 선택 계열사 — 미지정 시 도메인 기반 기본값 → 첫 항목 순 */
  defaultAffiliateId?: string;
  /** 승인 필터 옵트아웃 — 관리자 콘솔 '로그인 승인' 화면 전용.
   *  일반 화면에서 켜면 앱에 들어오지도 못하는 사람이 초대·멘션 후보에 섞인다. */
  includeUnapproved?: boolean;
}

export function useAffiliateUserSearch({
  isActive,
  currentUserId,
  excludeUserIds = [],
  defaultAffiliateId,
  includeUnapproved,
}: UseAffiliateUserSearchOptions) {
  // 계열사 관련 상태
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>('');
  const [isLoadingAffiliates, setIsLoadingAffiliates] = useState(false);

  // 사용자 검색 상태
  const [allUsers, setAllUsers] = useState<SelectableUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 계열사 목록 로딩 (한번만)
  useEffect(() => {
    if (!isActive) return;

    const load = async () => {
      setIsLoadingAffiliates(true);
      const list = await authService.getAffiliates();
      setAffiliates(list);
      if (list.length > 0 && !selectedAffiliateId) {
        const preferred = defaultAffiliateId && list.find((a) => a.id === defaultAffiliateId);
        const domainDefault = getDefaultAffiliateByDomain();
        const match = preferred || (domainDefault && list.find((a) => a.id === domainDefault));
        setSelectedAffiliateId(match ? match.id : list[0].id);
      }
      setIsLoadingAffiliates(false);
    };
    load();
  }, [isActive]); // eslint-disable-line

  // 계열사 변경 시 사용자 목록 로딩
  useEffect(() => {
    if (!isActive || !selectedAffiliateId) return;

    let cancelled = false;
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const users = await fetchAffiliateUsers(selectedAffiliateId, includeUnapproved);
        if (cancelled) return; // 계열사가 바뀌었거나 언마운트됨
        const selectableUsers = users
          .filter(
            (u) =>
              String(u.id) !== String(currentUserId) &&
              !excludeUserIds.includes(String(u.id))
          )
          .map((u) => ({ ...u, selected: false }));
        setAllUsers(selectableUsers);
      } catch (err) {
        // silent fallback 제거 — "비어있음 vs 에러"를 UI에서 구분
        // 401은 apiClient가 자동 로그아웃 트리거 → 모달이 unmount됨
        if (cancelled) return;
        console.error('Failed to fetch users for affiliate:', selectedAffiliateId, err);
        setError(err instanceof Error ? err.message : '사용자 목록을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsers();
    return () => { cancelled = true; };
  }, [isActive, selectedAffiliateId, currentUserId, includeUnapproved]); // eslint-disable-line

  // 검색 필터링
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return allUsers;
    const q = searchQuery.toLowerCase();
    return allUsers.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.depart?.toLowerCase().includes(q)
    );
  }, [allUsers, searchQuery]);

  // 선택된 사용자
  const selectedUsers = useMemo(() => allUsers.filter((u) => u.selected), [allUsers]);

  // 부서별 그룹화
  const usersByDepartment = useMemo(() => {
    const groups = new Map<string, SelectableUser[]>();
    filteredUsers.forEach((user) => {
      const dept = user.depart || '미배정';
      if (!groups.has(dept)) groups.set(dept, []);
      groups.get(dept)!.push(user);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredUsers]);

  // 토글
  const toggleUser = useCallback((userId: string | number) => {
    setAllUsers((prev) =>
      prev.map((u) =>
        String(u.id) === String(userId) ? { ...u, selected: !u.selected } : u
      )
    );
  }, []);

  // 선택 해제
  const removeUser = useCallback((userId: string | number) => {
    setAllUsers((prev) =>
      prev.map((u) =>
        String(u.id) === String(userId) ? { ...u, selected: false } : u
      )
    );
  }, []);

  // 부서 전체 선택
  const selectDepartment = useCallback((deptName: string) => {
    setAllUsers((prev) =>
      prev.map((u) =>
        u.depart === deptName || (!u.depart && deptName === '미배정')
          ? { ...u, selected: true }
          : u
      )
    );
  }, []);

  // 초기화 (모달 닫을 때 사용)
  const reset = useCallback(() => {
    setSearchQuery('');
    setAllUsers((prev) => prev.map((u) => ({ ...u, selected: false })));
  }, []);

  return {
    // 계열사
    affiliates,
    selectedAffiliateId,
    setSelectedAffiliateId,
    isLoadingAffiliates,
    showAffiliateSelector: affiliates.length > 1,

    // 사용자
    allUsers,
    filteredUsers,
    selectedUsers,
    usersByDepartment,
    loading,
    error,

    // 검색
    searchQuery,
    setSearchQuery,

    // 액션
    toggleUser,
    removeUser,
    selectDepartment,
    reset,
  };
}

/**
 * 계열사 명부 읽기 전용 훅 — 검색어·선택 상태 없이 목록만 필요한 화면용(온라인 패널).
 *
 * useAffiliateUserSearch와 **같은 모듈 캐시**를 공유하므로, 사용자가 홈에서 동료 검색을
 * 한 번이라도 열었던 세션에서는 왕복이 0이다(반대도 성립). 로그아웃 격리·아바타 패치도
 * 그 배관을 그대로 탄다 — 별도 캐시를 만들면 clearUserDirectoryCache를 복제해야 하고,
 * 빠뜨리면 다른 계정의 명부가 남는다.
 *
 * 초기값을 캐시에서 동기로 읽는 이유: 탭을 오갈 때마다 패널이 언마운트되는데, 재마운트에서
 * 빈 목록 → 채워짐으로 한 프레임 깜빡이면 목록이 튄다.
 */
export function useAffiliateDirectory(affiliateId?: string, isActive = true) {
  const [users, setUsers] = useState<User[]>(
    () => (affiliateId ? userDirectoryCache.get(directoryKey(affiliateId)) : undefined) ?? []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isActive || !affiliateId) return;

    const cached = userDirectoryCache.get(directoryKey(affiliateId));
    if (cached) {
      setUsers(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAffiliateUsers(affiliateId)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch directory for affiliate:', affiliateId, err);
        setError(err instanceof Error ? err.message : '명부를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [affiliateId, isActive]);

  return { users, loading, error };
}
